import {
  extractCarrySegments,
  insertSegments,
  readExifOrientation,
  readFrameInfo,
  setExifOrientation,
  stripCarrySegments,
} from './jpeg-segment';
import { getOrientationSpec, ORIENTATION_TOP_LEFT } from './orientation';
import { rotateLossless } from './lossless-rotate';
import { rotateWithCanvas } from './canvas-rotate';

/**
 * Exif の回転情報を画素に焼き込み、回転情報自体を削除するユーティリティ。
 *
 * Exif を解釈しない受け手でも正しい向きで扱えるようにするためのもので、
 * 回転が不要な画像には一切手を加えない (バイト単位で元のまま返す)。
 */

/**
 * canvas フォールバック時の再エンコード品質。
 *
 * 実写 2481x3508 での実測値:
 *   1.00 : PSNR 67.3dB / 最大差 3 (255階調) / 差が3以上の画素 0%   / サイズ元比 111%
 *   0.98 : PSNR 59.6dB / 最大差 5           / 差が3以上の画素 0.03% / サイズ元比  84%
 *
 * この経路は既にロスレスを諦めた少数派の画像に限られるため、
 * サイズよりも画質を優先して 1.0 を採用している。
 */
const FALLBACK_QUALITY = 1.0;

const JPEG_MIME_TYPES = ['image/jpeg', 'image/jpg'];

const readBytes = (file: File): Promise<Uint8Array> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
  reader.onerror = () => reject(new Error('画像を読み込めませんでした'));
  reader.readAsArrayBuffer(file);
});

/**
 * Exif の回転情報にしたがって画像を実回転させ、Orientation を 1 に落とした File を返す。
 *
 * 次の場合は元の File をそのまま返す (加工しない):
 *   - JPEG でない
 *   - Orientation が 1 または読み取れない
 *   - 変換に失敗した / 結果の検証に失敗した
 *
 * 呼び出し側でアップロードを止めないよう、例外は投げずに必ず File を返す。
 */
export const normalizeJpegOrientation = async (file: File): Promise<File> => {
  try {
    if (!JPEG_MIME_TYPES.includes(file.type)) {
      return file;
    }
    const bytes = await readBytes(file);
    const orientation = readExifOrientation(bytes);
    const spec = getOrientationSpec(orientation);
    if (!spec) {
      /* Orientation 1 (または不正値) は回転不要。一切加工しない */
      return file;
    }
    const frame = readFrameInfo(bytes);
    if (!frame) {
      console.error('[jpeg-orientation] SOF を読めないため元画像のまま扱います : ', file.name);
      return file;
    }

    const isLossless = spec.isLossless(frame);
    const rotated = isLossless
      ? await rotateLossless(bytes, spec.jpegtranArgs)
      : await rotateWithCanvas(bytes, frame, spec.canvasMatrix(frame.width, frame.height), spec.swapsAxis, FALLBACK_QUALITY);

    if (!rotated || !rotated.length) {
      console.error('[jpeg-orientation] 回転結果が空のため元画像のまま扱います : ', file.name);
      return file;
    }

    /* 結果の検証。想定した寸法になっていなければ採用しない */
    const expectedWidth = spec.swapsAxis ? frame.height : frame.width;
    const expectedHeight = spec.swapsAxis ? frame.width : frame.height;
    const rotatedFrame = readFrameInfo(rotated);
    if (!rotatedFrame || rotatedFrame.width !== expectedWidth || rotatedFrame.height !== expectedHeight) {
      console.error(
        '[jpeg-orientation] 回転結果の寸法が想定と異なるため元画像のまま扱います : ',
        file.name,
        { expectedWidth, expectedHeight, rotatedFrame },
      );
      return file;
    }

    /*
     * Exif / ICC / コメントを元ファイルから移植し、Orientation だけ 1 に落とす。
     * ロスレス経路は -copy all で引き継がれているが、二重化を避けるため
     * 一度すべて取り除いてから元のセグメントを入れ直し、両経路の扱いを揃える。
     */
    const carrySegments = extractCarrySegments(bytes)
      .map((segment) => setExifOrientation(segment, ORIENTATION_TOP_LEFT));
    const merged = insertSegments(stripCarrySegments(rotated), carrySegments);

    console.log(
      `[jpeg-orientation] ${file.name} : orientation ${orientation} を焼き込み `
      + `(${isLossless ? 'ロスレス' : 'canvas 再エンコード'}, `
      + `${frame.width}x${frame.height} -> ${expectedWidth}x${expectedHeight})`,
    );

    /* name と type は変えない。サーバ側のハッシュ判定が name に依存しているため */
    return new File([merged], file.name, { type: file.type });
  } catch (error) {
    console.error('[jpeg-orientation] 回転に失敗したため元画像のままアップロードします : ', file.name, error);
    return file;
  }
};
